import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, Grid, MenuItem, Snackbar, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoginIcon from "@mui/icons-material/Login";
import RefreshIcon from "@mui/icons-material/Refresh";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const tableSx = { border: "none", minHeight: 420, "& .MuiDataGrid-cell": { borderColor: "divider" }, "& .MuiDataGrid-columnHeaders": { borderColor: "divider" } };
const PAGE_SIZE = 50;

const usePagedRows = (loader) => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const payload = await loader({ page, limit: PAGE_SIZE });
      setRows(payload.data || []);
      setPagination(payload.pagination || { page, limit: PAGE_SIZE, total: payload.data?.length || 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loader]);
  useEffect(() => { load(1); }, [load]);
  return { rows, pagination, loading, error, load };
};

const Kpi = ({ label, value, tone = "primary" }) => (
  <Grid item xs={12} sm={6} md={3} lg={2}>
    <GlassCard hover={false}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" color={`${tone}.main`}>{value ?? 0}</Typography>
    </GlassCard>
  </Grid>
);

const Overview = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData(await saasApi.getSuperadminOverview()); setError(""); } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const s = data?.summary || {};
  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={2}>
        <Kpi label="Clientes" value={s.clients_total} />
        <Kpi label="Clientes activos" value={s.clients_active} tone="success" />
        <Kpi label="Usuarios" value={s.users_total} />
        <Kpi label="Camaras" value={s.cameras_total} />
        <Kpi label="Online" value={s.cameras_online} tone="success" />
        <Kpi label="Offline" value={s.cameras_offline} tone="warning" />
        <Kpi label="Alertas 24h" value={s.alerts_last_24h} />
        <Kpi label="Tickets abiertos" value={s.tickets_open} tone="warning" />
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <GlassCard hover={false}>
            <Typography variant="h6" mb={2}>Clientes con mas alertas</Typography>
            <DataGrid autoHeight rows={data?.top_clients || []} columns={[{ field: "name", headerName: "Cliente", flex: 1 }, { field: "alert_count", headerName: "Alertas 7d", width: 130 }]} sx={tableSx} hideFooter />
          </GlassCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <GlassCard hover={false}>
            <Typography variant="h6" mb={2}>Actividad reciente</Typography>
            <DataGrid autoHeight rows={(data?.alert_trend || []).map((row, id) => ({ id, ...row }))} columns={[{ field: "day", headerName: "Dia", flex: 1 }, { field: "count", headerName: "Eventos", width: 120 }]} sx={tableSx} hideFooter />
          </GlassCard>
        </Grid>
      </Grid>
      <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined">Actualizar</Button>
    </Stack>
  );
};

const Clients = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const loader = useCallback((params) => saasApi.getSuperadminClients({ ...params, q }), [q]);
  const { rows, pagination, loading, error, load } = usePagedRows(loader, [loader]);
  const columns = [
    { field: "name", headerName: "Cliente", flex: 1, minWidth: 180 },
    { field: "primary_email", headerName: "Email", flex: 1, minWidth: 190 },
    { field: "users_count", headerName: "Usuarios", width: 100 },
    { field: "branches_count", headerName: "Sucursales", width: 110 },
    { field: "cameras_count", headerName: "Camaras", width: 100 },
    { field: "cameras_online", headerName: "Online", width: 90 },
    { field: "cameras_offline", headerName: "Offline", width: 90 },
    { field: "status", headerName: "Estado", width: 120, renderCell: ({ value }) => <Chip label={value} size="small" /> },
  ];
  return (
    <GlassCard hover={false}>
      <Stack direction="row" gap={1} mb={2}>
        <TextField size="small" label="Buscar cliente" value={q} onChange={(event) => setQ(event.target.value)} />
        <Button variant="outlined" onClick={() => load(1)}>Buscar</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <DataGrid rows={rows} columns={columns} loading={loading} sx={tableSx} paginationMode="server" rowCount={pagination.total || 0} pageSizeOptions={[PAGE_SIZE]} initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } } }} onPaginationModelChange={(model) => load(model.page + 1)} onRowClick={(params) => navigate(`/superadmin/clients/${params.id}`)} disableRowSelectionOnClick />
    </GlassCard>
  );
};

const ClientDetail = () => {
  const location = useLocation();
  const id = location.pathname.match(/\/superadmin\/clients\/(\d+)/)?.[1];
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("users");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [manualCredit, setManualCredit] = useState({ period_start: "", period_end: "", amount: "", reason: "", overlap_resolution: "" });
  const [credentialName, setCredentialName] = useState("Motor de deteccion");
  const [credentials, setCredentials] = useState([]);
  const [newCredential, setNewCredential] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  useEffect(() => { saasApi.getSuperadminClient(id).then(setClient).catch((err) => setSnack({ open: true, message: err.message, severity: "error" })); }, [id]);
  const detailLoader = useCallback((params) => {
    if (tab === "cameras") return saasApi.getSuperadminClientCameras(id, params);
    if (tab === "detections") return saasApi.getSuperadminClientDetections(id, params);
    if (tab === "alerts") return saasApi.getSuperadminClientAlerts(id, params);
    return saasApi.getSuperadminClientUsers(id, params);
  }, [tab, id]);
  const { rows, pagination, loading, load } = usePagedRows(detailLoader);
  const columns = useMemo(() => {
    if (tab === "users") return [{ field: "email", headerName: "Email", flex: 1 }, { field: "role", headerName: "Rol", width: 130 }, { field: "is_active", headerName: "Activo", width: 100 }];
    if (tab === "detections") return [{ field: "detection_type", headerName: "Detector", flex: 1 }, { field: "camera_name", headerName: "Camara", flex: 1 }, { field: "enabled", headerName: "Activo", width: 100 }, { field: "version", headerName: "Version", width: 100 }];
    if (tab === "alerts") return [{ field: "detection_type", headerName: "Deteccion", flex: 1 }, { field: "severity", headerName: "Severidad", width: 120 }, { field: "status", headerName: "Estado", width: 140 }, { field: "occurred_at", headerName: "Fecha", flex: 1 }];
    return [{ field: "display_name", headerName: "Camara", flex: 1 }, { field: "branch_name", headerName: "Sucursal", flex: 1 }, { field: "status", headerName: "Estado", width: 120 }, { field: "last_heartbeat", headerName: "Heartbeat", flex: 1 }];
  }, [tab]);
  const commercial = client?.billing || {};
  const setAccessOverride = async (enabled) => {
    if (!overrideReason.trim()) return setSnack({ open: true, message: 'Indica un motivo para auditar el cambio.', severity: 'error' });
    try {
      await saasApi.setSuperadminOnboardingAccess(id, { enabled, reason: overrideReason.trim() });
      setClient(await saasApi.getSuperadminClient(id));
      setOverrideReason('');
      setSnack({ open: true, message: enabled ? 'Acceso excepcional habilitado.' : 'Excepcion revocada.', severity: 'success' });
    } catch (err) { setSnack({ open: true, message: err.message, severity: 'error' }); }
  };
  useEffect(() => {
    if (tab === "commercial" && id) saasApi.getSuperadminBillingCredentials(id).then((result) => setCredentials(result.data || [])).catch((err) => setSnack({ open: true, message: err.message, severity: "error" }));
  }, [tab, id]);
  const saveManualCredit = async () => {
    try {
      await saasApi.createSuperadminManualCoverage(id, {
        ...manualCredit,
        idempotency_key: window.crypto.randomUUID(),
      });
      setManualCredit({ period_start: "", period_end: "", amount: "", reason: "", overlap_resolution: "" });
      setClient(await saasApi.getSuperadminClient(id));
      setSnack({ open: true, message: "Cobertura manual acreditada y auditada; no se creo un cobro en Mercado Pago.", severity: "success" });
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const createServiceCredential = async () => {
    try {
      const result = await saasApi.createSuperadminBillingCredential(id, { name: credentialName });
      setNewCredential(result.token);
      setCredentials((previous) => [{ ...result, token: undefined, created_at: new Date().toISOString() }, ...previous]);
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const revokeServiceCredential = async (credentialId) => {
    try {
      await saasApi.revokeSuperadminBillingCredential(id, credentialId);
      setCredentials((previous) => previous.map((item) => item.id === credentialId ? { ...item, revoked_at: new Date().toISOString() } : item));
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const reviewBillingRequest = async (requestId, decision) => {
    if (decision === "approve" && !window.confirm("Aprobar esta configuracion y programarla para el siguiente ciclo? Si hay pago automatico, se sincronizara con Mercado Pago.")) return;
    try {
      await saasApi.reviewSuperadminBillingRequest(requestId, { decision });
      setClient(await saasApi.getSuperadminClient(id));
      setSnack({ open: true, message: decision === "approve" ? "Propuesta aprobada y programada." : "Propuesta rechazada.", severity: "success" });
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const updateProviderStatus = async (status) => {
    const action = status === "paused" ? "pausar" : "cancelar";
    if (!window.confirm(`Confirmar que queres ${action} el debito automatico en Mercado Pago? La cobertura ya acreditada no se modifica.`)) return;
    try {
      await saasApi.setSuperadminBillingSubscriptionStatus(id, { status });
      setClient(await saasApi.getSuperadminClient(id));
      setSnack({ open: true, message: `Debito automatico ${status === "paused" ? "pausado" : "cancelado"} en el proveedor.`, severity: "success" });
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const reconcileProvider = async () => {
    try {
      await saasApi.reconcileSuperadminBilling(id);
      setClient(await saasApi.getSuperadminClient(id));
      setSnack({ open: true, message: "Estado reconciliado con Mercado Pago.", severity: "success" });
    } catch (err) { setSnack({ open: true, message: err.message, severity: "error" }); }
  };
  const impersonate = async () => {
    try {
      const originalToken = localStorage.getItem("token");
      const originalUser = localStorage.getItem("user");
      const originalRole = localStorage.getItem("role");
      const data = await saasApi.startImpersonation({ organization_id: Number(id) });
      sessionStorage.setItem("superadmin_token", originalToken || "");
      sessionStorage.setItem("superadmin_user", originalUser || "");
      sessionStorage.setItem("superadmin_role", originalRole || "superadmin");
      sessionStorage.setItem("impersonation_session_id", String(data.session?.id || ""));
      sessionStorage.setItem("impersonation_client", data.client?.name || client?.client?.name || "");
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.effective_user));
      localStorage.setItem("role", data.effective_user?.role || "admin");
      window.location.assign("/overview");
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };
  return (
    <Stack spacing={2}>
      <Button component={Link} to="/superadmin/clients" variant="text">Volver a clientes</Button>
      <GlassCard hover={false}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Box>
            <Typography variant="h4">{client?.client?.name || `Cliente ${id}`}</Typography>
            <Typography color="text.secondary">{client?.client?.primary_email || client?.client?.billing_email || "Sin email principal"}</Typography>
          </Box>
          <Button variant="contained" startIcon={<LoginIcon />} onClick={impersonate}>Acceder como cliente</Button>
        </Stack>
      </GlassCard>
      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab value="users" label="Usuarios" />
        <Tab value="cameras" label="Camaras" />
        <Tab value="detections" label="Detecciones" />
        <Tab value="alerts" label="Alertas" />
        <Tab value="commercial" label="Comercial" />
      </Tabs>
      {tab === "commercial" ? <Stack spacing={2}>
        {client?.onboarding && <GlassCard hover={false}>
          <Typography variant="h6">Acceso excepcional al panel</Typography>
          <Alert severity="warning" sx={{ my: 1 }}>Esta excepcion permite entrar sin plan ni pago. No modifica camaras, motores ni cobros.</Alert>
          <Typography variant="body2" color="text.secondary">Estado: {client.onboarding.access_override_at ? 'Habilitado por Superadmin' : client.onboarding.dashboard_unlocked_at ? 'Desbloqueado por pago' : 'Contratacion pendiente'}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2}>
            <TextField fullWidth size="small" label="Motivo de la excepcion" value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} inputProps={{ maxLength: 500 }} />
            <Button variant="outlined" disabled={!overrideReason.trim()} onClick={() => setAccessOverride(!client.onboarding.access_override_at)}>
              {client.onboarding.access_override_at ? 'Revocar excepcion' : 'Permitir acceso sin plan'}
            </Button>
          </Stack>
        </GlassCard>}
        <Grid container spacing={2}>
          <Kpi label="Importe mensual vigente" value={commercial.contract?.monthly_price == null ? "Sin definir" : `${commercial.contract.currency || "ARS"} ${commercial.contract.monthly_price}`} />
          <Kpi label="Estado de suscripcion" value={commercial.contract?.subscription_status || "Sin suscripcion"} />
          <Kpi label="Cobertura hasta" value={commercial.contract?.paid_coverage_until ? new Date(commercial.contract.paid_coverage_until).toLocaleDateString("es-AR") : "Sin cobertura"} />
          <Kpi label="Proximo cobro" value={commercial.contract?.next_payment_at ? new Date(commercial.contract.next_payment_at).toLocaleDateString("es-AR") : "No programado"} />
          <Kpi label="Ultimo pago confirmado" value={commercial.contract?.last_confirmed_payment_at ? new Date(commercial.contract.last_confirmed_payment_at).toLocaleDateString("es-AR") : "Ninguno"} />
          <Kpi label="Proveedor" value={commercial.contract?.provider_sync_status || "No configurado"} />
        </Grid>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={1}>Contratacion vigente</Typography>
          {(commercial.items || []).length ? commercial.items.map((item) => <Typography key={item.id} variant="body2">{item.camera_name}: {item.component_type === "camera" ? "Camara" : item.detection_type} · {item.currency} {item.unit_amount} · {item.status}</Typography>) : <Typography color="text.secondary">Sin items comerciales migrados; los contratos anteriores requieren revision manual.</Typography>}
          {commercial.contract?.provider_preapproval_id && <Stack direction="row" spacing={1} mt={1}>
            <Button size="small" variant="outlined" onClick={reconcileProvider}>Reconciliar proveedor</Button>
            {commercial.contract.subscription_status === "authorized" && <Button size="small" color="warning" onClick={() => updateProviderStatus("paused")}>Pausar debito</Button>}
            {!['cancelled'].includes(commercial.contract.subscription_status) && <Button size="small" color="error" onClick={() => updateProviderStatus("cancelled")}>Cancelar suscripcion</Button>}
          </Stack>}
        </GlassCard>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={1}>Pagos y cobertura manual</Typography>
          {[...(commercial.payments || []).map((row) => ({ ...row, kind: row.provider })), ...(commercial.coverage || []).map((row) => ({ ...row, kind: row.entry_type }))].map((row, index) => <Typography key={`${row.kind}-${row.id}-${index}`} variant="body2">{row.kind}: {row.status} · {row.currency || "ARS"} {row.amount} · {row.period_start ? `${new Date(row.period_start).toLocaleDateString("es-AR")} - ${new Date(row.period_end).toLocaleDateString("es-AR")}` : ""}</Typography>)}
          {!commercial.payments?.length && !commercial.coverage?.length && <Typography color="text.secondary">Sin pagos ni coberturas registradas; la migracion no inventa pagos.</Typography>}
        </GlassCard>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={1}>Solicitudes comerciales</Typography>
          {(commercial.change_requests || []).length ? commercial.change_requests.map((request) => <Stack key={request.id} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} py={0.75}>
            <Typography variant="body2">#{request.id} · {request.status} · {request.quote_snapshot?.currency} {request.quote_snapshot?.amount} · {new Date(request.created_at).toLocaleString("es-AR")}</Typography>
            {["pending", "failed"].includes(request.status) && <Stack direction="row" spacing={1}><Button size="small" variant="contained" onClick={() => reviewBillingRequest(request.id, "approve")}>{request.status === "failed" ? "Reintentar aprobacion" : "Aprobar"}</Button><Button size="small" color="error" onClick={() => reviewBillingRequest(request.id, "reject")}>Rechazar</Button></Stack>}
          </Stack>) : <Typography color="text.secondary">No hay solicitudes.</Typography>}
        </GlassCard>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={1}>Acreditar cobertura manual</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>Esto registra cobertura manual, no un cobro de Mercado Pago. Si coincide con un débito programado, deberás resolver el solapamiento.</Alert>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={manualCredit.period_start} onChange={(event) => setManualCredit({ ...manualCredit, period_start: event.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={manualCredit.period_end} onChange={(event) => setManualCredit({ ...manualCredit, period_end: event.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label="Importe ARS" value={manualCredit.amount} onChange={(event) => setManualCredit({ ...manualCredit, amount: event.target.value })} /></Grid>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Motivo / referencia" value={manualCredit.reason} onChange={(event) => setManualCredit({ ...manualCredit, reason: event.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth select label="Resolucion de debito superpuesto" value={manualCredit.overlap_resolution} onChange={(event) => setManualCredit({ ...manualCredit, overlap_resolution: event.target.value })}>
              <MenuItem value="">No hay solapamiento conocido</MenuItem>
              <MenuItem value="automatic_debit_stopped">Proveedor confirmado pausado/cancelado</MenuItem>
              <MenuItem value="risk_acknowledged">Acepto el riesgo de doble cobro</MenuItem>
            </TextField></Grid>
            <Grid item xs={12}><Button variant="contained" onClick={saveManualCredit} disabled={!manualCredit.period_start || !manualCredit.period_end || !manualCredit.amount || !manualCredit.reason}>Acreditar periodo</Button></Grid>
          </Grid>
        </GlassCard>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={1}>Credenciales de solo lectura del motor</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1}>
            <TextField size="small" label="Nombre" value={credentialName} onChange={(event) => setCredentialName(event.target.value)} />
            <Button variant="outlined" onClick={createServiceCredential}>Crear credencial</Button>
          </Stack>
          {newCredential && <Alert severity="warning" sx={{ mb: 1 }}>Copiala ahora; no se volvera a mostrar: <code>{newCredential}</code></Alert>}
          {credentials.map((item) => <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
            <Typography variant="body2">{item.name} · creada {new Date(item.created_at).toLocaleDateString("es-AR")} · {item.revoked_at ? "Revocada" : "Activa"}</Typography>
            {!item.revoked_at && <Button size="small" color="error" onClick={() => revokeServiceCredential(item.id)}>Revocar</Button>}
          </Stack>)}
        </GlassCard>
      </Stack> : <GlassCard hover={false}>
        <DataGrid rows={rows} columns={columns} loading={loading} sx={tableSx} paginationMode="server" rowCount={pagination.total || 0} pageSizeOptions={[PAGE_SIZE]} initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } } }} onPaginationModelChange={(model) => load(model.page + 1)} disableRowSelectionOnClick />
      </GlassCard>}
      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}><Alert severity={snack.severity}>{snack.message}</Alert></Snackbar>
    </Stack>
  );
};

const GlobalTable = ({ title, loader, columns, filters }) => {
  const stableLoader = useCallback(loader, [loader]);
  const { rows, pagination, loading, error, load } = usePagedRows(stableLoader);
  return (
    <GlassCard hover={false}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5">{title}</Typography>
        <Button startIcon={<RefreshIcon />} onClick={() => load(pagination.page || 1)}>Actualizar</Button>
      </Stack>
      {filters}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <DataGrid rows={rows} columns={columns} loading={loading} sx={tableSx} paginationMode="server" rowCount={pagination.total || 0} pageSizeOptions={[PAGE_SIZE]} initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } } }} onPaginationModelChange={(model) => load(model.page + 1)} disableRowSelectionOnClick />
    </GlassCard>
  );
};

const useClientOptions = () => {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    saasApi.getSuperadminClients({ page: 1, limit: 100 }).then((payload) => setClients(payload.data || [])).catch(() => setClients([]));
  }, []);
  return clients;
};

const FilteredGlobalTable = ({ title, type, loader, columns }) => {
  const clients = useClientOptions();
  const [filters, setFilters] = useState({ q: "", organization_id: "", branch_id: "", status: "", severity: "", detection_type: "" });
  const [applied, setApplied] = useState(filters);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!filters.organization_id || type !== "cameras") {
      setBranches([]);
      return;
    }
    saasApi.getBranches({ organization_id: filters.organization_id }).then(setBranches).catch(() => setBranches([]));
  }, [filters.organization_id, type]);

  const activeLoader = useCallback((params) => {
    const cleanFilters = Object.fromEntries(Object.entries(applied).filter(([, value]) => value !== ""));
    return loader({ ...params, ...cleanFilters });
  }, [loader, applied]);

  const filterControls = (
    <Stack direction={{ xs: "column", md: "row" }} gap={1} mb={2} flexWrap="wrap">
      <TextField size="small" label="Buscar" value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value })} />
      <TextField select size="small" label="Cliente" value={filters.organization_id} onChange={(event) => setFilters({ ...filters, organization_id: event.target.value, branch_id: "" })} sx={{ minWidth: 190 }}>
        <MenuItem value="">Todos</MenuItem>
        {clients.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>)}
      </TextField>
      {type === "cameras" && (
        <>
          <TextField select size="small" label="Sucursal" value={filters.branch_id} onChange={(event) => setFilters({ ...filters, branch_id: event.target.value })} sx={{ minWidth: 170 }}>
            <MenuItem value="">Todas</MenuItem>
            {branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })} sx={{ minWidth: 140 }}>
            <MenuItem value="">Todos</MenuItem>
            {["online", "offline", "error", "maintenance"].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
          </TextField>
        </>
      )}
      {type === "alerts" && (
        <>
          <TextField select size="small" label="Severidad" value={filters.severity} onChange={(event) => setFilters({ ...filters, severity: event.target.value })} sx={{ minWidth: 140 }}>
            <MenuItem value="">Todas</MenuItem>
            {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((severity) => <MenuItem key={severity} value={severity}>{severity}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })} sx={{ minWidth: 150 }}>
            <MenuItem value="">Todos</MenuItem>
            {["nueva", "en_revision", "confirmada", "falsa_alarma", "resuelta"].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
          </TextField>
        </>
      )}
      {(type === "detections" || type === "alerts") && (
        <TextField size="small" label="Deteccion" value={filters.detection_type} onChange={(event) => setFilters({ ...filters, detection_type: event.target.value })} />
      )}
      <Button variant="contained" onClick={() => setApplied(filters)}>Filtrar</Button>
      <Button variant="outlined" onClick={() => { const empty = { q: "", organization_id: "", branch_id: "", status: "", severity: "", detection_type: "" }; setFilters(empty); setApplied(empty); }}>Limpiar</Button>
    </Stack>
  );

  return <GlobalTable title={title} loader={activeLoader} columns={columns} filters={filterControls} />;
};

const Health = () => {
  const [data, setData] = useState(null);
  useEffect(() => { saasApi.getSuperadminHealth().then(setData).catch(() => setData(null)); }, []);
  return (
    <Grid container spacing={2}>
      {Object.entries(data || {}).map(([key, value]) => (
        <Grid item xs={12} md={4} key={key}>
          <GlassCard hover={false}>
            <Typography variant="h6">{key}</Typography>
            <Chip label={value?.status || "unknown"} color={value?.status === "healthy" ? "success" : value?.status === "critical" ? "error" : "warning"} />
            <Typography variant="body2" color="text.secondary" mt={1}>{JSON.stringify(value)}</Typography>
          </GlassCard>
        </Grid>
      ))}
    </Grid>
  );
};

const Support = () => {
  const clients = useClientOptions();
  const [filters, setFilters] = useState({ q: "", organization_id: "", status: "", priority: "" });
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");

  const loadConversations = useCallback(async () => {
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));
      const payload = await saasApi.getSuperadminSupportConversations({ page: 1, limit: 50, ...clean });
      setConversations(payload.data || []);
      setSelected((current) => current || payload.data?.[0] || null);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [filters]);

  const loadMessages = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const payload = await saasApi.getSuperadminSupportMessages(selected.id, { page: 1, limit: 100 });
      setMessages(payload.data || []);
    } catch (err) {
      setError(err.message);
    }
  }, [selected]);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    loadMessages();
    const id = setInterval(() => { if (document.visibilityState === "visible") loadMessages(); }, 4000);
    return () => clearInterval(id);
  }, [loadMessages]);

  const sendReply = async () => {
    if (!selected?.id || !reply.trim()) return;
    try {
      await saasApi.sendSuperadminSupportMessage(selected.id, { message: reply });
      setReply("");
      await loadMessages();
      await loadConversations();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateConversation = async (patch) => {
    if (!selected?.id) return;
    try {
      await saasApi.updateSuperadminSupportConversation(selected.id, patch);
      setSelected({ ...selected, ...patch });
      await loadConversations();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Stack spacing={2}>
    <Stack direction="row" justifyContent="flex-end">
      <Button component={Link} to="/superadmin/support/knowledge" variant="outlined">Administrar respuestas automaticas</Button>
    </Stack>
    <Grid container spacing={2}>
      <Grid item xs={12} lg={4}>
        <GlassCard hover={false}>
          <Typography variant="h6" mb={2}>Conversaciones</Typography>
          <Stack spacing={1} mb={2}>
            <TextField size="small" label="Buscar cliente/asunto" value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value })} />
            <TextField select size="small" label="Cliente" value={filters.organization_id} onChange={(event) => setFilters({ ...filters, organization_id: event.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {clients.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>)}
            </TextField>
            <Stack direction="row" gap={1}>
              <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })} fullWidth>
                <MenuItem value="">Todos</MenuItem>
                {["OPEN", "IN_PROGRESS", "WAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Prioridad" value={filters.priority} onChange={(event) => setFilters({ ...filters, priority: event.target.value })} fullWidth>
                <MenuItem value="">Todas</MenuItem>
                {["LOW", "NORMAL", "HIGH", "URGENT"].map((priority) => <MenuItem key={priority} value={priority}>{priority}</MenuItem>)}
              </TextField>
            </Stack>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadConversations}>Buscar</Button>
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={1}>
            {conversations.map((item) => (
              <Button key={item.id} variant={selected?.id === item.id ? "contained" : "outlined"} onClick={() => setSelected(item)} sx={{ justifyContent: "space-between", textAlign: "left" }}>
                <Box component="span">{item.organization_name} · {item.subject}</Box>
                {item.unread_count ? <Chip label={item.unread_count} color="error" size="small" /> : null}
              </Button>
            ))}
          </Stack>
        </GlassCard>
      </Grid>
      <Grid item xs={12} lg={8}>
        <GlassCard hover={false}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1} mb={2}>
            <Box>
              <Typography variant="h5">{selected?.subject || "Selecciona una conversacion"}</Typography>
              {selected && <Typography color="text.secondary">{selected.organization_name} · {selected.created_by_email}</Typography>}
            </Box>
            {selected && (
              <Stack direction="row" gap={1}>
                <TextField select size="small" label="Estado" value={selected.status || "OPEN"} onChange={(event) => updateConversation({ status: event.target.value })} sx={{ minWidth: 170 }}>
                  {["OPEN", "IN_PROGRESS", "WAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Prioridad" value={selected.priority || "NORMAL"} onChange={(event) => updateConversation({ priority: event.target.value })} sx={{ minWidth: 140 }}>
                  {["LOW", "NORMAL", "HIGH", "URGENT"].map((priority) => <MenuItem key={priority} value={priority}>{priority}</MenuItem>)}
                </TextField>
              </Stack>
            )}
          </Stack>
          <Stack spacing={1.5} sx={{ minHeight: 390, maxHeight: 520, overflowY: "auto", pr: 1 }}>
            {messages.map((message) => (
              <Box key={message.id} sx={{ maxWidth: "82%", alignSelf: message.sender_role === "superadmin" ? "flex-end" : "flex-start", p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "8px", bgcolor: message.sender_role === "superadmin" ? "primary.main" : "action.hover", color: message.sender_role === "superadmin" ? "primary.contrastText" : "text.primary" }}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>{message.sender_role} · {message.created_at}</Typography>
                <Typography>{message.message}</Typography>
              </Box>
            ))}
            {!messages.length && <Typography color="text.secondary">No hay mensajes para mostrar.</Typography>}
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} gap={1} mt={2}>
            <TextField label="Responder al cliente" value={reply} onChange={(event) => setReply(event.target.value)} fullWidth disabled={!selected} />
            <Button variant="contained" onClick={sendReply} disabled={!selected || !reply.trim()}>Enviar</Button>
          </Stack>
        </GlassCard>
      </Grid>
    </Grid>
    </Stack>
  );
};

const emptyKnowledgeArticle = { id: null, category: "General", title: "", keywords: "", answer: "", related_terms: "", is_active: true, sort_order: 0 };

const parseDelimitedFile = (text) => {
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const delimiter = [",", ";", "\t"].sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0];
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"' && quoted && text[i + 1] === '"') { cell += '"'; i += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === delimiter && !quoted) { row.push(cell); cell = ""; }
    else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell); cell = "";
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
    } else cell += char;
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
};

const parseXlsxFile = async (buffer) => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let endRecord = -1;
  for (let offset = bytes.length - 22; offset >= Math.max(0, bytes.length - 65557); offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) { endRecord = offset; break; }
  }
  if (endRecord < 0) throw new Error("El archivo no parece ser un libro XLSX valido");
  const entryCount = view.getUint16(endRecord + 10, true);
  let directoryOffset = view.getUint32(endRecord + 16, true);
  const decoder = new TextDecoder("utf-8");
  const entries = new Map();
  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(directoryOffset, true) !== 0x02014b50) throw new Error("No se pudo leer la estructura del archivo XLSX");
    const method = view.getUint16(directoryOffset + 10, true);
    const compressedSize = view.getUint32(directoryOffset + 20, true);
    const nameLength = view.getUint16(directoryOffset + 28, true);
    const extraLength = view.getUint16(directoryOffset + 30, true);
    const commentLength = view.getUint16(directoryOffset + 32, true);
    const localOffset = view.getUint32(directoryOffset + 42, true);
    const name = decoder.decode(bytes.subarray(directoryOffset + 46, directoryOffset + 46 + nameLength));
    entries.set(name, { method, compressedSize, localOffset });
    directoryOffset += 46 + nameLength + extraLength + commentLength;
  }
  const readEntry = async (name) => {
    const entry = entries.get(name);
    if (!entry) return "";
    const local = entry.localOffset;
    const nameLength = view.getUint16(local + 26, true);
    const extraLength = view.getUint16(local + 28, true);
    const start = local + 30 + nameLength + extraLength;
    const contents = bytes.slice(start, start + entry.compressedSize);
    if (entry.method === 0) return decoder.decode(contents);
    if (entry.method !== 8 || typeof DecompressionStream === "undefined") throw new Error("No se puede descomprimir este XLSX en este navegador. Guardalo como CSV UTF-8.");
    const stream = new Blob([contents]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    return decoder.decode(await new Response(stream).arrayBuffer());
  };
  const parseXml = (xml) => {
    const document = new DOMParser().parseFromString(xml, "application/xml");
    if (document.querySelector("parsererror")) throw new Error("El libro XLSX contiene XML invalido");
    return document;
  };
  const workbook = parseXml(await readEntry("xl/workbook.xml"));
  const relationshipId = Array.from(workbook.getElementsByTagName("sheet")[0]?.attributes || []).find((attribute) => attribute.localName === "id")?.value;
  if (!relationshipId) throw new Error("El libro XLSX no contiene una hoja");
  const relationships = parseXml(await readEntry("xl/_rels/workbook.xml.rels"));
  const relation = Array.from(relationships.getElementsByTagName("Relationship")).find((item) => item.getAttribute("Id") === relationshipId);
  if (!relation) throw new Error("No se encontro la primera hoja del libro XLSX");
  const target = relation.getAttribute("Target").replace(/^\//, "");
  const sheetPath = target.startsWith("xl/") ? target : `xl/${target.replace(/^\.\//, "")}`;
  const sharedXml = await readEntry("xl/sharedStrings.xml");
  const sharedStrings = sharedXml ? Array.from(parseXml(sharedXml).getElementsByTagName("si")).map((item) => Array.from(item.getElementsByTagName("t")).map((text) => text.textContent || "").join("")) : [];
  const sheet = parseXml(await readEntry(sheetPath));
  const columnNumber = (reference) => {
    const letters = String(reference || "").match(/^[A-Z]+/i)?.[0]?.toUpperCase() || "A";
    return [...letters].reduce((sum, letter) => sum * 26 + letter.charCodeAt(0) - 64, 0) - 1;
  };
  return Array.from(sheet.getElementsByTagName("row")).map((row) => {
    const values = [];
    Array.from(row.getElementsByTagName("c")).forEach((cell) => {
      const column = columnNumber(cell.getAttribute("r"));
      const type = cell.getAttribute("t");
      const raw = cell.getElementsByTagName("v")[0]?.textContent || "";
      const value = type === "s" ? sharedStrings[Number(raw)] || "" : type === "inlineStr" ? Array.from(cell.getElementsByTagName("t")).map((text) => text.textContent || "").join("") : type === "b" ? raw === "1" ? "true" : "false" : raw;
      values[column] = value;
    });
    return values.map((value) => value || "");
  }).filter((row) => row.some((value) => String(value).trim()));
};

const knowledgeHeaderKey = (value) => String(value || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const KnowledgeBase = () => {
  const fileInput = useRef(null);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(emptyKnowledgeArticle);
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("all");
  const [listPage, setListPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ message: "", severity: "success" });
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const parseTerms = (value) => {
    if (Array.isArray(value)) return value;
    try { const parsed = JSON.parse(value || "[]"); return Array.isArray(parsed) ? parsed : []; }
    catch (_) { return String(value || "").split(/[,;\n|]/).map((term) => term.trim()).filter(Boolean); }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const payload = await saasApi.getSuperadminSupportKnowledge({ page: listPage, limit: 100, q: filter, status: status === "all" ? "" : status });
      setArticles(payload.data || []);
      setPagination(payload.pagination || { total: payload.data?.length || 0, page: 1, total_pages: 1 });
    } catch (err) { setNotice({ message: err.message, severity: "error" }); }
    finally { setBusy(false); }
  }, [filter, status, listPage]);
  useEffect(() => { load(); }, [load]);

  const selectArticle = (item) => setForm({
    ...item,
    keywords: parseTerms(item.keywords).join(", "),
    related_terms: parseTerms(item.related_terms).join(", "),
    is_active: item.is_active === true || item.is_active === 1 || item.is_active === "1",
  });
  const payloadFromForm = () => ({
    ...form,
    keywords: form.keywords.split(/[,;\n|]/).map((word) => word.trim()).filter(Boolean),
    related_terms: form.related_terms.split(/[,;\n|]/).map((word) => word.trim()).filter(Boolean),
    sort_order: Number(form.sort_order || 0),
  });
  const saveArticle = async () => {
    if (!form.title.trim() || !form.answer.trim() || !form.keywords.trim()) return;
    setBusy(true);
    try {
      if (form.id) await saasApi.updateSuperadminSupportKnowledge(form.id, payloadFromForm());
      else await saasApi.createSuperadminSupportKnowledge(payloadFromForm());
      setNotice({ message: "Respuesta guardada", severity: "success" });
      setForm(emptyKnowledgeArticle);
      await load();
    } catch (err) { setNotice({ message: err.message, severity: "error" }); }
    finally { setBusy(false); }
  };
  const deleteArticle = async () => {
    if (!form.id) return;
    setBusy(true);
    try {
      await saasApi.deleteSuperadminSupportKnowledge(form.id);
      setNotice({ message: "Respuesta eliminada", severity: "success" });
      setForm(emptyKnowledgeArticle);
      await load();
    } catch (err) { setNotice({ message: err.message, severity: "error" }); }
    finally { setBusy(false); }
  };
  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      setNotice({ message: "Formato no compatible. Carga un archivo XLSX o CSV.", severity: "warning" });
      return;
    }
    setBusy(true);
    try {
      const rows = /\.xlsx$/i.test(file.name) ? await parseXlsxFile(await file.arrayBuffer()) : parseDelimitedFile((await file.text()).replace(/^\uFEFF/, ""));
      if (rows.length < 2) throw new Error("El archivo no contiene filas de respuestas");
      const headers = rows[0].map(knowledgeHeaderKey);
      const findColumn = (...names) => headers.findIndex((key) => names.includes(key));
      const indexes = {
        category: findColumn("categoria", "category"), title: findColumn("titulo", "title", "pregunta"),
        keywords: findColumn("palabras_clave", "keywords", "keyword", "disparadores"),
        answer: findColumn("respuesta", "answer"), related_terms: findColumn("terminos_relacionados", "related_terms", "relacionados"),
        is_active: findColumn("activo", "is_active", "active"), sort_order: findColumn("orden", "sort_order", "order"),
      };
      if ([indexes.title, indexes.keywords, indexes.answer].some((index) => index < 0)) throw new Error("Faltan columnas obligatorias: titulo, palabras_clave y respuesta");
      const imported = rows.slice(1).map((line) => ({
        category: indexes.category < 0 ? "General" : line[indexes.category],
        title: line[indexes.title], keywords: line[indexes.keywords], answer: line[indexes.answer],
        related_terms: indexes.related_terms < 0 ? "" : line[indexes.related_terms],
        is_active: indexes.is_active < 0 ? true : !["0", "false", "no", "inactivo"].includes(String(line[indexes.is_active]).toLowerCase()),
        sort_order: indexes.sort_order < 0 ? 0 : Number(line[indexes.sort_order] || 0),
      })).filter((item) => item.title?.trim() && item.answer?.trim() && item.keywords?.trim());
      const result = await saasApi.importSuperadminSupportKnowledge(imported);
      setNotice({ message: `Se importaron ${result.imported} respuestas`, severity: "success" });
      await load();
    } catch (err) { setNotice({ message: err.message, severity: "error" }); }
    finally { setBusy(false); }
  };
  const exportFile = async () => {
    setBusy(true);
    try {
      const first = await saasApi.getSuperadminSupportKnowledge({ page: 1, limit: 100 });
      const all = [...(first.data || [])];
      for (let page = 2; page <= (first.pagination?.total_pages || 1); page += 1) {
        const next = await saasApi.getSuperadminSupportKnowledge({ page, limit: 100 });
        all.push(...(next.data || []));
      }
      const rows = [["categoria", "titulo", "palabras_clave", "respuesta", "terminos_relacionados", "activo", "orden"], ...all.map((item) => [
        item.category, item.title, parseTerms(item.keywords).join(" | "), item.answer,
        parseTerms(item.related_terms).join(" | "), Number(item.is_active) ? "true" : "false", item.sort_order,
      ])];
      const csv = "\uFEFF" + rows.map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = "respuestas-soporte.csv"; anchor.click(); URL.revokeObjectURL(url);
      setNotice({ message: `Se exportaron ${all.length} respuestas en CSV compatible con Excel`, severity: "success" });
    } catch (err) { setNotice({ message: err.message, severity: "error" }); }
    finally { setBusy(false); }
  };
  const downloadTemplate = () => {
    const csv = "\uFEFFcategoria,titulo,palabras_clave,respuesta,terminos_relacionados,activo,orden\r\n\"Camaras\",\"Camara trabada\",\"camara trabada | video congelado\",\"Indica los pasos de ayuda para el cliente.\",\"sin imagen | camara lenta\",true,0";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = "plantilla-respuestas-soporte.csv"; anchor.click(); URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Button component={Link} to="/superadmin/support" startIcon={<ArrowBackIcon />} variant="text">Volver a conversaciones</Button>
        <Stack direction="row" gap={1}>
          <input ref={fileInput} hidden type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={importFile} />
          <Button variant="text" onClick={downloadTemplate} disabled={busy}>Plantilla CSV</Button>
          <Button variant="outlined" onClick={() => fileInput.current?.click()} disabled={busy}>Importar Excel / CSV</Button>
          <Button variant="outlined" onClick={exportFile} disabled={busy}>Exportar CSV</Button>
        </Stack>
      </Stack>
      {notice.message && <Alert severity={notice.severity} onClose={() => setNotice({ message: "", severity: "success" })}>{notice.message}</Alert>}
      <Typography variant="body2" color="text.secondary">Columnas requeridas: titulo, palabras_clave y respuesta. Se admite XLSX o CSV UTF-8; la primera hoja del Excel es la que se importa.</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <GlassCard hover={false}>
            <Typography variant="h6" mb={2}>Base de respuestas ({pagination.total})</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1} mb={2}>
              <TextField size="small" label="Buscar" value={filter} onChange={(event) => { setFilter(event.target.value); setListPage(1); }} fullWidth />
              <TextField select size="small" label="Estado" value={status} onChange={(event) => { setStatus(event.target.value); setListPage(1); }} sx={{ minWidth: 130 }}>
                <MenuItem value="all">Todos</MenuItem><MenuItem value="active">Activos</MenuItem><MenuItem value="inactive">Inactivos</MenuItem>
              </TextField>
            </Stack>
            <Stack spacing={1} sx={{ maxHeight: 560, overflowY: "auto" }}>
              {articles.map((item) => <Button key={item.id} onClick={() => selectArticle(item)} variant={form.id === item.id ? "contained" : "outlined"} sx={{ justifyContent: "space-between", textAlign: "left" }}><span>{item.title}</span><Chip size="small" label={item.category} /></Button>)}
              {!articles.length && <Typography color="text.secondary">No hay respuestas con esos filtros.</Typography>}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                <Button disabled={listPage <= 1 || busy} onClick={() => setListPage((page) => page - 1)}>Anterior</Button>
                <Typography variant="caption">Pagina {listPage} de {pagination.total_pages || 1}</Typography>
                <Button disabled={listPage >= (pagination.total_pages || 1) || busy} onClick={() => setListPage((page) => page + 1)}>Siguiente</Button>
              </Stack>
            </Stack>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <GlassCard hover={false}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{form.id ? "Editar respuesta" : "Nueva respuesta"}</Typography>
              <Button onClick={() => setForm(emptyKnowledgeArticle)}>Nueva</Button>
            </Stack>
            <Stack spacing={1.5}>
              <TextField label="Categoria" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
              <TextField label="Titulo o pregunta" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              <TextField label="Palabras clave" helperText="Separadas por coma. Inclui formas habituales de describir el mismo problema." value={form.keywords} onChange={(event) => setForm({ ...form, keywords: event.target.value })} />
              <TextField label="Respuesta" multiline minRows={5} value={form.answer} onChange={(event) => setForm({ ...form, answer: event.target.value })} />
              <TextField label="Terminos relacionados" helperText="Ayudan a sugerir otras respuestas despues de abrir esta." value={form.related_terms} onChange={(event) => setForm({ ...form, related_terms: event.target.value })} />
              <Stack direction="row" gap={2} alignItems="center">
                <TextField label="Orden" type="number" value={form.sort_order || 0} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} sx={{ width: 130 }} />
                <FormControlLabel control={<Checkbox checked={Boolean(form.is_active)} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} />} label="Publicado" />
              </Stack>
              <Stack direction="row" gap={1}>
                <Button variant="contained" onClick={saveArticle} disabled={busy || !form.title.trim() || !form.answer.trim() || !form.keywords.trim()}>Guardar</Button>
                {form.id && <Button color="error" onClick={deleteArticle} disabled={busy}>Eliminar</Button>}
              </Stack>
            </Stack>
          </GlassCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

const BILLING_DETECTION_TYPES = ["shoplifting", "fall", "fire", "smoke", "weapon", "people_count", "queue", "heatmap", "region", "perimeter", "pet"];

const BillingCatalog = () => {
  const [data, setData] = useState({ versions: [] });
  const [prices, setPrices] = useState({ camera: "" });
  const [note, setNote] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState({ data: [], pagination: { page: 1, limit: 50, total: 0 }, versionId: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const load = useCallback(() => saasApi.getSuperadminBillingCatalog().then(setData).catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);
  const saveDraft = async () => {
    const rows = [{ component_type: "camera", amount: prices.camera }, ...BILLING_DETECTION_TYPES
      .filter((type) => String(prices[type] || "").trim() !== "")
      .map((type) => ({ component_type: "detection", detection_type: type, amount: prices[type] }))];
    setBusy(true); setError(""); setNotice("");
    try { const result = await saasApi.createSuperadminBillingCatalogDraft({ prices: rows, note }); setDraft(result); setNotice(`Borrador v${result.version_number} guardado. No cambia contratos existentes.`); await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const publishDraft = async () => {
    if (!draft || !effectiveAt || preview.versionId !== draft.id) return;
    if (!window.confirm(`Publicar v${draft.version_number} desde ${new Date(effectiveAt).toLocaleString("es-AR")}? Los contratos no cambiaran automaticamente. Se notificara a los clientes.`)) return;
    setBusy(true); setError(""); setNotice("");
    try { await saasApi.publishSuperadminBillingCatalog(draft.id, { effective_at: new Date(effectiveAt).toISOString() }); setDraft(null); setNotice("Tarifario publicado. No modifica contratos automaticamente."); await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const loadPreview = async (page = 1) => {
    if (!draft) return;
    setBusy(true); setError("");
    try { setPreview({ ...(await saasApi.previewSuperadminBillingCatalog(draft.id, { page, limit: 50 })), versionId: draft.id }); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <Stack spacing={2}>
    {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
    <GlassCard hover={false}>
      <Typography variant="h5" mb={1}>Catalogo de precios ARS</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>Cada version requiere publicacion explicita y fecha de vigencia. Los contratos no se actualizan al cambiar el catalogo.</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label="Precio por camara / mes" value={prices.camera} onChange={(event) => setPrices({ ...prices, camera: event.target.value })} /></Grid>
        {BILLING_DETECTION_TYPES.map((type) => <Grid item xs={12} sm={6} md={4} key={type}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label={`${type} por camara / mes`} value={prices[type] || ""} onChange={(event) => setPrices({ ...prices, [type]: event.target.value })} /></Grid>)}
        <Grid item xs={12}><TextField fullWidth label="Nota de version" value={note} onChange={(event) => setNote(event.target.value)} /></Grid>
      </Grid>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={2}>
        <Button variant="outlined" disabled={busy || prices.camera === "" || BILLING_DETECTION_TYPES.some((type) => prices[type] === undefined || prices[type] === "")} onClick={saveDraft}>Guardar borrador</Button>
        {draft && <><TextField size="small" type="datetime-local" label="Vigencia futura" InputLabelProps={{ shrink: true }} value={effectiveAt} onChange={(event) => setEffectiveAt(event.target.value)} /><Button variant="outlined" disabled={busy} onClick={() => loadPreview(1)}>Previsualizar impacto</Button><Button variant="contained" disabled={busy || !effectiveAt || preview.versionId !== draft.id} onClick={publishDraft}>Confirmar y publicar v{draft.version_number}</Button></>}
      </Stack>
      {draft && <Box mt={2}>
        <Typography variant="subtitle1" mb={1}>Impacto estimado · {preview.pagination.total || 0} clientes</Typography>
        <DataGrid autoHeight rows={preview.data || []} columns={[
          { field: "name", headerName: "Cliente", flex: 1, minWidth: 170 },
          { field: "current_monthly", headerName: "Actual (ARS)", width: 140 },
          { field: "proposed_monthly", headerName: "Propuesto (ARS)", width: 150 },
          { field: "difference", headerName: "Diferencia", width: 120 },
          { field: "direction", headerName: "Cambio", width: 130 },
        ]} paginationMode="server" rowCount={preview.pagination.total || 0} pageSizeOptions={[50]} initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }} onPaginationModelChange={(model) => loadPreview(model.page + 1)} />
      </Box>}
    </GlassCard>
    {(data.versions || []).map((version) => <GlassCard key={version.id} hover={false}>
      <Typography variant="h6">Version {version.version_number} · {version.status}</Typography>
      <Typography variant="body2" color="text.secondary">Vigencia: {version.effective_at ? new Date(version.effective_at).toLocaleString("es-AR") : "Borrador"} · {version.note || "Sin nota"}</Typography>
      <Typography variant="body2" mt={1}>{(version.prices || []).map((price) => `${price.component_type === "camera" ? "Camara" : price.detection_type}: ARS ${price.amount}`).join(" · ")}</Typography>
    </GlassCard>)}
  </Stack>;
};

const SuperadminConsole = () => {
  const location = useLocation();
  const section = location.pathname.split("/")[2] || "overview";
  const content = (() => {
    if (location.pathname.match(/^\/superadmin\/clients\/\d+/)) return <ClientDetail />;
    if (section === "clients") return <Clients />;
    if (section === "billing") return <BillingCatalog />;
    if (section === "cameras") return <FilteredGlobalTable type="cameras" title="Camaras globales" loader={(params) => saasApi.getSuperadminCameras(params)} columns={[{ field: "organization_name", headerName: "Cliente", flex: 1 }, { field: "branch_name", headerName: "Sucursal", flex: 1 }, { field: "display_name", headerName: "Camara", flex: 1 }, { field: "status", headerName: "Estado", width: 120 }, { field: "last_heartbeat", headerName: "Heartbeat", flex: 1 }]} />;
    if (section === "detections") return <FilteredGlobalTable type="detections" title="Detecciones globales" loader={(params) => saasApi.getSuperadminDetections(params)} columns={[{ field: "organization_name", headerName: "Cliente", flex: 1 }, { field: "camera_name", headerName: "Camara", flex: 1 }, { field: "detection_type", headerName: "Detector", flex: 1 }, { field: "enabled", headerName: "Activo", width: 100 }, { field: "version", headerName: "Version", width: 100 }]} />;
    if (section === "alerts") return <FilteredGlobalTable type="alerts" title="Alertas globales" loader={(params) => saasApi.getSuperadminAlerts(params)} columns={[{ field: "organization_name", headerName: "Cliente", flex: 1 }, { field: "camera_name", headerName: "Camara", flex: 1 }, { field: "detection_type", headerName: "Deteccion", flex: 1 }, { field: "severity", headerName: "Severidad", width: 120 }, { field: "status", headerName: "Estado", width: 140 }, { field: "occurred_at", headerName: "Fecha", flex: 1 }]} />;
    if (section === "support" && location.pathname.endsWith("/knowledge")) return <KnowledgeBase />;
    if (section === "support") return <Support />;
    if (section === "system") return <Health />;
    if (section === "audit") return <GlobalTable title="Auditoria global" loader={(params) => saasApi.getSuperadminAudit(params)} columns={[{ field: "created_at", headerName: "Fecha", flex: 1 }, { field: "username", headerName: "Usuario", flex: 1 }, { field: "organization_name", headerName: "Cliente", flex: 1 }, { field: "action", headerName: "Accion", flex: 1 }, { field: "entity_type", headerName: "Recurso", width: 140 }]} />;
    return <Overview />;
  })();
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Header title="Superadmin" subtitle="Administracion global de plataforma" />
      <Tabs value={section} variant="scrollable" sx={{ mb: 3 }}>
        {[["overview", "/superadmin", "Overview"], ["clients", "/superadmin/clients", "Clientes"], ["billing", "/superadmin/billing", "Precios y contratos"], ["cameras", "/superadmin/cameras", "Infraestructura"], ["detections", "/superadmin/detections", "Detecciones"], ["alerts", "/superadmin/alerts", "Alertas"], ["support", "/superadmin/support", "Soporte"], ["system", "/superadmin/system", "Sistema"], ["audit", "/superadmin/audit", "Auditoria"]].map(([value, to, label]) => (
          <Tab key={value} value={value} label={label} component={Link} to={to} />
        ))}
      </Tabs>
      {content}
    </Box>
  );
};

export default SuperadminConsole;
